/**
 * Cross-Category-Drag = Reclassify. Das Drop-Ziel entscheidet die Achse: ein Ordner **derselben**
 * Top-Kategorie = Move (bestehendes Verhalten), ein anderer = Reclassify (Bedeutungswechsel —
 * Finanz-Zuordnung/Sichtbarkeit). Reine Entscheidungsschicht ohne UI/IO: `decideDrop` liefert
 * move / silent / dialog / none.
 *
 * Stille (unbestätigte) Reclassify feuert NUR, wenn das gezogene Ziel ein **platz-stabiler**
 * Scaffold-Ordner ist — einer, für den der Server `BuildFolder(kind)` exakt diesen Pfad zurückgibt
 * (kein Datums-/Zustands-Re-Filing). Nur dann landet die Datei garantiert dort, wo sie fallen
 * gelassen wurde. Datums-Kategorien, Vertrag-Aktiv/Archiv (aus `validTo` abgeleitet) und
 * Custom-Ordner können cross-kategorial nicht platz-stabil geraten werden → immer Dialog.
 */
import type { PropertyDocumentKind, PropertyFileResponse } from '@/shared/api';
import { bulkCategory } from './akteBulkRules';
import { topFolderForPath } from './akteFolderMeta';
import {
  CATEGORY_ROOTS,
  categoryOf,
  isCategoryMoveTarget,
  isSameCategory,
  isSystemFolder,
  isValidMoveTarget,
  segmentsOf,
} from './akteFolderRules';

/**
 * Umkehrung von `PropertyFilePathBuilder.BuildFolder` für die platz-stabilen Scaffold-Leaves:
 * Ordner, deren kanonischer Pfad ein Kind eindeutig festlegt UND für die der Server denselben
 * Pfad zurückrechnet. Bewusst OHNE die datums-abgeleiteten Kategorien (03/04/05-Jahr/06) und OHNE
 * Vertrag-Aktiv/Archiv (aus `validTo` abgeleitet) — dort wäre die Ablage nicht der gedroppte
 * Ordner. Nur diese sind für stille Reclassify zugelassen.
 */
const FIXED_PLACEMENT_KIND: Record<string, PropertyDocumentKind> = {
  '01_Stammakte': 'Stammakte',
  '05_Steuer-Behoerde/Stamm': 'BehoerdeStamm',
  '07_Inventar/Listen': 'InventarListe',
  '07_Inventar/Garantien': 'Garantie',
  '07_Inventar/Bedienungsanleitungen': 'Bedienungsanleitung',
  '08_Fotos/Marketing': 'FotoMarketing',
  '08_Fotos/Zustand-Checkin': 'FotoZustand',
  '08_Fotos/Inventar': 'FotoInventar',
  '08_Fotos/Schaeden': 'FotoSchaden',
  '08_Fotos/Reinigung': 'FotoReinigung',
  '09_Schluessel-Zugang': 'SchluesselZugang',
  '99_Sonstiges': 'Sonstiges',
};

const CATEGORY_ROOT_SET = new Set(CATEGORY_ROOTS);

/**
 * Das Kind, das ein Ziel-Ordner **eindeutig und platz-stabil** bestimmt, oder `null`, wenn er das
 * nicht tut (System-Ordner, datums-/zustands-abgeleitete oder Custom-Ordner). `null` → das Ziel
 * taugt nicht für eine stille Reclassify, es braucht den Dialog.
 */
export function inferKindFromFolder(folder: string): PropertyDocumentKind | null {
  if (isSystemFolder(folder)) return null; // Ausgehend/AusgangsRechnung: nie per Reclassify
  return FIXED_PLACEMENT_KIND[folder] ?? null;
}

/**
 * Best-Effort-Kind für die Dialog-Vorbelegung: das Kind des nächsten platz-stabilen Ordner-
 * Vorfahren (z.B. Custom-Ordner unter `07_Inventar/Garantien` → Garantie), sonst das Vorgabe-Kind
 * der Ziel-Kategorie (`topFolderForPath`). `null`, wenn kein echter Kategorie-Ordner — dann fällt
 * der Dialog auf das aktuelle Kind der Datei zurück.
 */
export function suggestKindForFolder(folder: string): PropertyDocumentKind | null {
  const segments = segmentsOf(folder);
  for (let n = segments.length; n >= 1; n--) {
    const ancestorKind = FIXED_PLACEMENT_KIND[segments.slice(0, n).join('/')];
    if (ancestorKind) return ancestorKind;
  }
  const category = categoryOf(folder);
  if (category === null || !CATEGORY_ROOT_SET.has(category)) return null;
  return topFolderForPath(folder).defaultKind;
}

/** Achsen-Entscheidung eines Drops. `kind` bei `dialog` = Vorbelegung (kann `null` sein). */
export type DropDecision =
  | { action: 'move' }
  | { action: 'silent'; kind: PropertyDocumentKind }
  | { action: 'dialog'; kind: PropertyDocumentKind | null }
  | { action: 'none' };

/** Entscheidet, was ein Drop von `sourceFolder` auf `targetFolder` auslöst. */
export function decideDrop(sourceFolder: string, targetFolder: string): DropDecision {
  // System-Ziele (GoBD-Ausgehend) sind für Drag komplett tabu.
  if (isSystemFolder(targetFolder)) return { action: 'none' };

  // Selbe Top-Kategorie = Move; nur echte Unterordner sind gültige Ziele.
  if (isSameCategory(sourceFolder, targetFolder)) {
    return isValidMoveTarget(sourceFolder, targetFolder) ? { action: 'move' } : { action: 'none' };
  }

  // Cross-Kategorie = Reclassify — nur in echte Kategorie-Ordner (Fantasie-Pfade abweisen).
  const targetCategory = categoryOf(targetFolder);
  if (targetCategory === null || categoryOf(sourceFolder) === null) return { action: 'none' };
  if (!CATEGORY_ROOT_SET.has(targetCategory)) return { action: 'none' };

  const kind = inferKindFromFolder(targetFolder);
  // Platz-stabiles Ziel → still (Datei landet garantiert hier). Sonst Dialog, vorbelegt.
  return kind !== null
    ? { action: 'silent', kind }
    : { action: 'dialog', kind: suggestKindForFolder(targetFolder) };
}

/** Achse für ein **Mehrfach**-Payload (Drag einer Auswahl > 1). Kein `silent` — Bulk-Reclassify
 *  läuft immer über den Dialog (Bedeutungsänderung vieler Belege ohne Undo → Bestätigung). */
export type BatchDropAction = 'move' | 'reclassify' | 'none';

/**
 * Entscheidet, was ein Drop der ganzen Auswahl `files` auf `targetFolder` auslöst: Bulk-Move nur bei
 * homogener Kategorie in einen gültigen Unterordner derselben Kategorie; sonst (andere Kategorie
 * oder gemischte Auswahl) Bulk-Reclassify (via Dialog); System-/Nicht-Kategorie-Ziele = none.
 */
export function decideBatchDrop(
  files: readonly PropertyFileResponse[],
  targetFolder: string,
): BatchDropAction {
  if (isSystemFolder(targetFolder)) return 'none';
  const targetCategory = categoryOf(targetFolder);
  if (targetCategory === null || !CATEGORY_ROOT_SET.has(targetCategory)) return 'none';

  const sourceCategory = bulkCategory(files); // gemeinsame Quell-Kategorie oder null (heterogen)
  if (sourceCategory !== null && sourceCategory === targetCategory) {
    // Selbe Kategorie → Move (nur echte Unterordner, kein Root/System) UND nur, wenn sich mindestens
    // eine Datei bewegt — sonst wäre das Ziel ein Highlight ohne Effekt (Einzelpfad guard'et via `!==`).
    const movable =
      isCategoryMoveTarget(sourceCategory, targetFolder) &&
      files.some((f) => f.folder !== targetFolder);
    return movable ? 'move' : 'none';
  }
  // Andere Kategorie oder gemischte Auswahl → Reclassify auf die Ziel-Kategorie.
  return 'reclassify';
}
